// src/pages/Terms.js
// Terms of Service Page

import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../components/common';
import './Upgrade.css';

function Terms() {

  return (
    <div className="upgrade-container">
      <SEOHead
        title="Terms of Service | DriftAlarm"
        description="DriftAlarm Terms of Service covering scanning authorization, AI analysis, API access, subscription tiers, data retention, and liability for our EASM platform."
        path="/terms"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Terms of Service — DriftAlarm",
            "url": "https://www.driftalarm.com/terms",
            "dateModified": "2026-03-13T00:00:00-05:00",
            "publisher": {
              "@type": "Organization",
              "name": "DriftAlarm",
              "url": "https://www.driftalarm.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.driftalarm.com/images/og-image.png"
              },
              "foundingDate": "2026",
              "sameAs": ["https://www.driftalarm.com"]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
              { "@type": "ListItem", "position": 2, "name": "Terms of Service" }
            ]
          }
        ]}
      />
      <PublicHeader activeLink={null} />

      {/* Main Content */}
      <div className="upgrade-content legal-content">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last Updated: March 2026</p>

        <section className="legal-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using DriftAlarm ("the Service"), you agree to be bound by these Terms of Service,
            our <Link to="/privacy">Privacy Policy</Link>, our <Link to="/acceptable-use">Acceptable Use Policy</Link>,
            and (if applicable) our <Link to="/dpa">Data Processing Agreement</Link>. If you are accepting on behalf
            of an organization, you represent that you have the authority to bind that organization to these Terms.
            You must be at least 18 years old to use the Service.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            DriftAlarm is an AI-enabled External Attack Surface Management (EASM) platform. The Service provides:
          </p>
          <ul>
            <li><strong>Fast Scan</strong> — rapid security assessment of domains and IP addresses</li>
            <li><strong>Deep Scan</strong> — comprehensive vulnerability analysis including DAST testing, SSL/TLS auditing, and web server analysis (Standard+ tiers)</li>
            <li><strong>Asset Monitoring</strong> — continuous automated monitoring of domains and IP ranges with inventory tracking</li>
            <li><strong>Drift Detection</strong> — automatic detection of configuration changes across ports, subdomains, certificates, DNS, and findings with customizable alarm rules</li>
            <li><strong>AI-Powered Analysis</strong> — AI-generated remediation guidance, risk scoring, executive reports, and technology analysis powered by Anthropic's Claude models</li>
            <li><strong>Notification &amp; Alerting</strong> — email, Slack webhook, and generic webhook notifications for drift events and scan results</li>
            <li><strong>Interactive Security Tools</strong> — DNS, SSL, port scanning, and WHOIS/RDAP lookup tools</li>
            <li><strong>API Access</strong> — programmatic access via API keys for integration with your security workflows (Standard+ tiers)</li>
            <li><strong>Scheduled Scanning</strong> — automated recurring scans on configurable schedules</li>
            <li><strong>Reporting</strong> — weekly and monthly trend reports with AI analysis</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Account Registration and Security</h2>
          <p>
            Accounts are created through Microsoft Azure AD External ID (CIAM). You are responsible for maintaining
            the security of your account. You must promptly notify us of any unauthorized access. Account sharing
            is prohibited.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Subscription Tiers and Payment</h2>
          <ul>
            <li><strong>Trial:</strong> $0 for 30 days, full Standard-tier capabilities, 1 domain + 1 IP, automatically expires</li>
            <li><strong>Standard:</strong> $99/month or $999/year, 1 domain + 1 IP, API access (5 keys, 60 req/min)</li>
            <li><strong>Pro:</strong> $389/month, 5 domains + 10 IP ranges, advanced features (Deep Security, Drift Analytics, Inventory, Validate), API access (10 keys, 300 req/min)</li>
            <li><strong>Enterprise:</strong> Custom pricing, contact <a href="mailto:sales@driftalarm.com">sales@driftalarm.com</a></li>
          </ul>
          <p>
            All fees are non-refundable except as required by law. We reserve the right to modify pricing with
            30 days' email notice. Tier feature availability is subject to current platform capabilities. Standard
            and Pro subscriptions are purchased and managed self-serve, with payments processed automatically by our
            payment processor, Stripe; you can upgrade, downgrade, or cancel at any time from your account settings.
            Enterprise plans are arranged directly with our team.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Scanning Authorization</h2>
          <p>
            You represent and warrant that you have authorization to scan all targets (domains, IP addresses,
            IP ranges) submitted to DriftAlarm. Authorization means: (a) you own the asset, OR (b) you have
            explicit written permission from the asset owner.
          </p>
          <p>
            DriftAlarm performs active reconnaissance including DNS enumeration, port scanning, web crawling,
            vulnerability detection, SSL/TLS testing, and technology fingerprinting. You are solely responsible
            for ensuring your scanning activities comply with all applicable laws.
          </p>
          <p>
            DriftAlarm is not responsible for any consequences of scanning unauthorized targets. We reserve the
            right to suspend scanning for any target if we receive a complaint or have reason to believe scanning
            is unauthorized.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. AI-Powered Analysis</h2>
          <p>DriftAlarm uses Anthropic's Claude AI models to provide:</p>
          <ul>
            <li>Remediation guidance for discovered vulnerabilities</li>
            <li>Risk scoring and analysis</li>
            <li>Executive security reports</li>
            <li>Technology stack analysis</li>
          </ul>
          <p>
            AI-generated content is advisory only and should not be treated as professional security advice.
            You should verify all AI recommendations before implementing changes. Scan data (findings, technology
            data, configurations) is sent to Anthropic's API for processing — see our <Link to="/privacy">Privacy
            Policy</Link> for details. Anthropic's commercial API does not use customer data for model training.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. API Access</h2>
          <p>
            API keys are available on Standard+ tiers. API keys use a <code>da_live_</code> prefix and are
            SHA-256 hashed for storage — the full key is shown only once at creation. You are responsible for
            the security of your API keys.
          </p>
          <p>
            API key usage is subject to per-tier rate limits (Standard: 60/min, Pro: 300/min, Enterprise: 600/min).
            API keys cannot be used for key management operations (CIAM authentication required). DriftAlarm may
            revoke API keys that are compromised, abused, or used in violation of these Terms. See our{' '}
            <Link to="/acceptable-use">Acceptable Use Policy</Link> for API usage guidelines.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Drift Detection and Alerting</h2>
          <p>
            DriftAlarm automatically detects changes to your attack surface by comparing scan results against
            established baselines. 34 built-in alarm rules across 7 categories monitor for security-relevant
            changes. Custom alarm rules are available (Trial: 5, Standard: 10, Pro: 25, Enterprise: unlimited).
          </p>
          <p>
            Notifications are delivered via your configured channels (email, Slack, webhooks). Notification
            delivery is provided on a best-effort basis — DriftAlarm does not guarantee delivery of all alerts.
            You are responsible for configuring and maintaining your notification channels.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Data Retention</h2>
          <ul>
            <li><strong>Scan result files:</strong> stored in Azure Blob Storage with lifecycle policy (30 days active → 30 days Cool tier → deletion)</li>
            <li><strong>Asset findings:</strong> retained while asset is active, deleted when asset is removed</li>
            <li><strong>Drift events and baselines:</strong> retained while asset is active</li>
            <li><strong>User account data:</strong> retained while account is active, deleted within 90 days of account closure</li>
            <li><strong>Weekly/monthly reports:</strong> retained per blob lifecycle policy</li>
          </ul>
          <p>See our <Link to="/privacy">Privacy Policy</Link> for complete data handling details.</p>
        </section>

        <section className="legal-section">
          <h2>10. Third-Party Services</h2>
          <p>DriftAlarm integrates with the following third-party services:</p>
          <ul>
            <li><strong>Microsoft Azure</strong> — cloud hosting, storage, authentication (East US region)</li>
            <li><strong>Anthropic</strong> — AI-powered analysis via Claude API (commercial terms, no training on customer data)</li>
            <li><strong>GreyNoise</strong> — IP reputation and noise classification (24-hour cache)</li>
            <li><strong>Certificate Transparency Logs</strong> — subdomain discovery via crt.sh</li>
            <li><strong>RDAP/WHOIS</strong> — domain registration data lookup</li>
            <li><strong>Microsoft Graph API</strong> — email delivery for notifications and onboarding</li>
          </ul>
          <p>
            Each service is subject to its own terms and privacy policies. DriftAlarm is not responsible for the
            availability or accuracy of third-party service data.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Intellectual Property</h2>
          <p>
            The Service, including its software, design, user interface, and documentation, is owned by DriftAlarm
            and protected by intellectual property laws. You retain ownership of all data you submit for scanning.
            Scan results, findings, and analysis generated by the Service are licensed to you for your internal use
            only. You may not reverse engineer, decompile, or create derivative works from the Service.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Limitation of Liability</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. DRIFTALARM.AI SHALL
            NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL
            LIABILITY SHALL NOT EXCEED THE AMOUNT PAID FOR THE SERVICE IN THE PRECEDING 12 MONTHS.
          </p>
          <p>
            DRIFTALARM.AI IS NOT LIABLE FOR: (a) vulnerabilities missed by scanning, (b) false positives or false
            negatives, (c) consequences of acting on AI recommendations, (d) unauthorized access to your targets
            resulting from scanning, (e) third-party service interruptions.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless DriftAlarm from any claims, damages, losses, or expenses
            arising from:
          </p>
          <ul>
            <li>Your use of the Service</li>
            <li>Unauthorized scanning activities</li>
            <li>Violation of these Terms or the Acceptable Use Policy</li>
            <li>Your negligence or willful misconduct</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>14. Termination</h2>
          <p>
            We may suspend or terminate your access for violation of these Terms or the{' '}
            <Link to="/acceptable-use">Acceptable Use Policy</Link>. Upon termination: access is revoked
            immediately, data is deleted per retention policy (within 90 days). You may cancel your subscription
            at any time from your account settings, or contact <a href="mailto:support@driftalarm.com">support@driftalarm.com</a> for help.
          </p>
          <p>
            Sections that survive termination: Limitation of Liability, Indemnification, Governing Law.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes will be communicated via email to your
            registered address and posted on the Service. Continued use after changes constitutes acceptance. We
            will provide at least 30 days' notice for material changes.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Texas, United States, without regard to conflict
            of law principles. Any disputes shall be resolved in the courts located in Texas. You agree to submit
            to the personal jurisdiction of such courts.
          </p>
        </section>

        <section className="legal-section">
          <h2>17. Contact</h2>
          <p>
            For questions about these Terms, contact us at{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>. For Enterprise pricing and sizing, contact{' '}
            <a href="mailto:sales@driftalarm.com">sales@driftalarm.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>18. Related Policies</h2>
          <ul>
            <li><Link to="/privacy">Privacy Policy</Link></li>
            <li><Link to="/acceptable-use">Acceptable Use Policy</Link></li>
            <li><Link to="/dpa">Data Processing Agreement</Link></li>
          </ul>
        </section>
      </div>

      <PublicFooter />
    </div>
  );
}

export default Terms;
